'use client';

import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { TemplateSettings } from '@eliadmin/core/src/api/general/types/template-settings-types';
import { MobileSearchProvider } from '@eliadmin/core/src/components/header/mobile-search';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';
import Link from '@eliadmin/core/src/components/link';
import { LuCalendarPlus } from 'react-icons/lu';
import { HeaderCartButton } from './header-cart-button';
import { HeaderMenu } from './header-menu';
import MobileSidebar from './mobile-sidebar';
import { usePathname } from 'next/navigation';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { HeaderProfileButton } from '@eliadmin/core/src/components/header/header-profile-button';
import { DynamicLogo } from '@eliadmin/core/src/components/header/dynamic-logo';

export type HeaderMainProps = {
  generalLayout: GeneralLayout['data'];
  templateSettings: TemplateSettings['data'];
  className?: {
    root?: string;
    container?: string;
    toolsNavigation?: string;
    logo?: string;
    mobileMenuButton?: string;
    searchIcon?: string;
    search?: string;
    headerCategoriesButton?: string;
    headerMenu?: string;
    headerToolIcons?: string;
    profile?: string;
    contact?: string;
    cart?: string;
    socialIcons?: string;
    customHeader?: string;
    language?: string;
  };
  menu: Menu[];
};

export function HeaderMain({ templateSettings, generalLayout, menu }: HeaderMainProps) {
  const pathname = usePathname();
  const isHomepage = pathname === ROUTES.HOME();

  return (
    <MobileSearchProvider>
      <div
        className={cn(
          'custom-section ea-header-main static py-3 sm:py-6',
          isHomepage ? 'border-none bg-transparent' : 'bg-white',
        )}
        style={{ order: templateSettings.header?.tools_navigation?.order }}
      >
        {/* Desktop */}
        <div
          className={cn(
            'ea-header-main__desktop',
            'hidden w-full flex-col items-center justify-center gap-8 md:flex',
            'custom-container',
          )}
        >
          <div className="flex w-full items-center justify-between">
            <div className="flex w-full flex-1">
              <Button
                asChild
                className={
                  isHomepage
                    ? 'border border-white bg-transparent text-white hover:bg-white/20'
                    : ''
                }
              >
                <Link prefetchOnHover prefetch={false} href={'/rezervacia'}>
                  <LuCalendarPlus size={20} /> REZERVÁCIA
                </Link>
              </Button>
            </div>

            <DynamicLogo generalLayout={generalLayout} />

            <div className="flex w-full flex-1 items-center justify-end gap-3">
              <HeaderProfileButton isHomepage={isHomepage} />
              <HeaderCartButton
                autoOpenMediaQuery="(min-width: 768px)"
                className={
                  isHomepage
                    ? {
                        root: 'border-white text-white hover:bg-white/10 hover:text-white',
                        price: 'text-white',
                      }
                    : undefined
                }
              />
              <Link
                prefetchOnHover
                prefetch={false}
                href={'/eshop'}
                className={cn(
                  'ea-header-main__eshop-button',
                  'flex h-12 items-center justify-center px-8 font-medium',
                  isHomepage
                    ? 'border border-white bg-transparent text-white hover:bg-white/20'
                    : 'bg-primary text-white',
                )}
              >
                ESHOP
              </Link>
            </div>
          </div>
          <HeaderMenu menu={menu} isHomepage={isHomepage} />
        </div>

        {/* Mobile */}
        <div
          className={cn(
            'ea-header-main__mobile',
            'flex w-full items-center justify-between md:hidden',
            'custom-container',
          )}
        >
          <DynamicLogo generalLayout={generalLayout} />
          <div className="flex flex-1 items-center justify-end gap-2">
            <MobileSidebar
              side="left"
              menu={menu[0] && menu[0].fields}
              className={isHomepage ? { trigger: 'text-white' } : undefined}
            />
          </div>
        </div>
      </div>
    </MobileSearchProvider>
  );
}
